import { useEffect, useState } from "react";
import type { AdminEntry } from "@server/admins/types";
import { ApiError, useAdmins, useMe, useSaveAdmins } from "../api/client";
import { Button, ErrorBanner } from "../components/ui";
import { WidePanel } from "../components/Workspace";

// An admin already saved shows as text; a new one is typed in. `entry` is set only for the saved ones.
interface Row {
  email: string;
  entry?: AdminEntry;
}

const dayOf = (iso: string): string => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

// Who can manage the form lists, the incident responders and this list. The ones set in the code are always admins.
export function AdminUsers() {
  const stored = useAdmins();
  const save = useSaveAdmins();
  const me = useMe();
  const [rows, setRows] = useState<Row[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (stored.data) {
      setRows(stored.data.admins.map((entry) => ({ email: entry.email, entry })));
    }
  }, [stored.data]);

  const myEmail = me.data?.email?.toLowerCase();
  const error =
    save.error instanceof ApiError
      ? save.error.issues.length > 0
        ? [...new Set(save.error.issues)].join(". ")
        : save.error.message
      : save.error
        ? "Something went wrong. Please try again."
        : undefined;

  const change = (index: number, email: string): void => {
    setSaved(false);
    setRows((current) => current.map((row, position) => (position === index ? { ...row, email } : row)));
  };

  return (
    <WidePanel>
      <div className="phase-head">
        <h1>Admins</h1>
        <p>
          Admins can edit the form lists and the incident responders, see every ticket and incident, resolve incidents and change this list. They sign in with their @vestd.com Google account. A
          change applies within about a minute.
        </p>
      </div>

      {stored.isError && <ErrorBanner>We couldn't load the admins.</ErrorBanner>}
      {stored.data && (
        <form
          className="issue-form"
          onSubmit={(event) => {
            event.preventDefault();
            const emails = rows.map((row) => row.email.trim().toLowerCase()).filter((email) => email !== "");
            save.mutate({ emails, version: stored.data.version }, { onSuccess: () => setSaved(true) });
          }}
        >
          <div className="admin-built-in">
            <p className="field-legend">Always admins</p>
            <p className="hint">Set in the code, so they can't be removed here. Everyone below was added in this page.</p>
            <ul className="cat-chips">
              {stored.data.builtIn.map((email) => (
                <li key={email} className="cat-chip">
                  {email}
                </li>
              ))}
              {stored.data.builtIn.length === 0 && <li className="hint">None</li>}
            </ul>
          </div>

          <div className="admin-rows">
            <p className="field-legend">Added here</p>
            {rows.length === 0 && <p className="record-note">No one added yet.</p>}
            {rows.map((row, index) => {
              const yourself = row.entry !== undefined && row.email === myEmail;
              return (
                <div className="admin-row" key={row.entry?.email ?? `new-${index}`}>
                  {row.entry ? (
                    <span className="admin-email">{row.email}</span>
                  ) : (
                    <input type="email" aria-label="Email" placeholder="name@vestd.com" autoFocus value={row.email} onChange={(e) => change(index, e.target.value)} />
                  )}
                  <span className="hint">{row.entry ? `Added by ${row.entry.addedBy} on ${dayOf(row.entry.addedAt)}` : "Not saved yet"}</span>
                  <Button
                    type="button"
                    variant="secondary"
                    className="btn-sm"
                    disabled={yourself}
                    title={yourself ? "You can't remove yourself. Ask another admin" : undefined}
                    onClick={() => {
                      setSaved(false);
                      setRows((current) => current.filter((_, position) => position !== index));
                    }}
                  >
                    Remove
                  </Button>
                </div>
              );
            })}
          </div>

          {error && <ErrorBanner>{error}</ErrorBanner>}
          {saved && <p className="record-note">Saved. It applies within about a minute.</p>}
          {stored.data.updatedBy && (
            <p className="hint">
              Last changed by {stored.data.updatedBy}
              {stored.data.updatedAt ? ` on ${new Date(stored.data.updatedAt).toLocaleString("en-GB")}` : ""}.
            </p>
          )}
          <div className="form-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setSaved(false);
                setRows((current) => [...current, { email: "" }]);
              }}
            >
              Add an admin
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save the list"}
            </Button>
          </div>
        </form>
      )}
    </WidePanel>
  );
}
